<script>
  import { mapState } from 'vuex'
  import Button from '@/components/ui/Button'
  import Loader from '@/components/Loader'
  import TheHeader from '@/components/ProtectedBag/partials/TheHeader'
  import TheModal from '@/components/ProtectedBag/partials/TheModal'
  import FloatBar from '@/components/ProtectedBag/partials/FloatBar'
  import GoBackButton from '@/components/ProtectedBag/partials/GoBackButton'
  import ModalArticle from '@/components/ProtectedBag/partials/ModalArticle'

  import { getBanks } from '@/api/services.js'
  import { createSession } from '@/utils/createSession.js'

  export default {
    name: 'ProtectedBagAccount',
    components: {
      Button,
      FloatBar,
      TheHeader,
      Loader,
      TheModal,
      GoBackButton,
      ModalArticle
    },
    mounted () {
      sessionStorage.removeItem('ciclic:checkout')

      if (!this.quotedDevice) {
        this.redirectToHome()
      }

      this.getBanks()
    },
    data () {
      return {
        isLoadingPage: false,
        isLoadingBanks: true,
        openModal: false,
        formIsValid: false,
        accountInfos: {
          agency: '',
          account: '',
          bankCode: ''
        },
        banks: []
      }
    },
    computed: {
      ...mapState(['appSettings', 'quotedDevice']),
      maxOptionAllowedPriceDevice () {
        const insuranceTypeList = this.quotedDevice.somenteRoubo ? 'theftPaymentOptions' : 'completePaymentOptions'
        return this.currency(this.quotedDevice.device[insuranceTypeList].at(-1))
      },
      maxOptionAllowedPriceBag () {
        return this.currency(this.protectedBag.bagOptions.at(-1))
      },
      goToPaymentButtonAvailable () {
        return this.formIsValid && this.accountInfos.agency && this.accountInfos.account && this.accountInfos.bankCode
      },
      protectedBag () {
        return this.quotedDevice.promoBag
      }
    },
    methods: {
      preventSpecialCharacteres (e) {
        if (/[`!@#$%^&*()_+=\\[\]{};':"\\|,.<>\\/?~]/.test(e.key)) {
          e.preventDefault()
        }
      },
      getBanks () {
        this.isLoadingBanks = true
        getBanks()
          .then((response) => {
            this.banks = response.data
          })
          .catch((error) => {
            this.$sentry.logWithContext({
              ctx: { step: '[step/protectedBagAccount]: error ' }, error
            })
          })
          .finally(() => {
            this.isLoadingBanks = false
          })
      },
      currency (value) {
        return 'R$ ' + Number(value).toFixed(2).replace('.', ',').toString()
      },
      redirectToHome () {
        this.$router.push({ name: 'Home' })
      },
      goToCheckout () {
        createSession({
          ...this.quotedDevice,
          protectedBag: {
            accountInfos: this.accountInfos,
            termsLink: 'https://files.ciclic.com.br/general_conditions/assurant-bolsa-e-conta-protegida-dez-2022.pdf',
            ...this.protectedBag
          }
        }, this.$sentry)

        const hasMpeWeek = this.appSettings.partner && this.appSettings.partner.slug === 'mpeweek'
        const checkoutLink = hasMpeWeek
          ? `${process.env.VUE_APP_HOME_URL}checkout/#/dados-da-empresa`
          : `${process.env.VUE_APP_HOME_URL}checkout/#/dados-pessoais`

        setTimeout(() => {
          window.location.assign(checkoutLink)
        }, 1000)
      },
      toggleModal () {
        this.openModal = !this.openModal
      }
    }
  }
</script>

<template>
  <div id="protected-bag-account">
    <Loader v-if="isLoadingPage" />

    <TheHeader />

    <b-container>
      <GoBackButton />

      <section class="content">
        <h2 class="content__title">
          Adicione os <mark class="content__mark">dados da conta</mark> que será protegida
        </h2>

        <div class="content__form">
          <v-form v-model="formIsValid" @submit.prevent="">
            <div class="content__fieldset">
              <label class="content__label">Banco</label>
              <v-combobox
                v-model="accountInfos.bankCode"
                :placeholder="isLoadingBanks ? 'Carregando...' : 'Selecione o banco'"
                :loading="isLoadingBanks"
                :items="banks"
                :disabled="banks.length === 0"
                required
                outlined
              ></v-combobox>
            </div>
            <div class="content__fieldset">
              <label class="content__label">Agência</label>
              <v-text-field
                @keydown="preventSpecialCharacteres($event)"
                v-model="accountInfos.agency"
                placeholder="Digite a agência"
                maxlength="10"
                :rules="[
                  (v) => v.length > 0 || 'Por favor, digite uma agência',
                  (v) => /^[a-zA-Z0-9-\-]+$/.test(v) || 'Digite apenas números, letras ou hífen',
                ]"
                outlined
                required
              />
            </div>
            <div class="content__fieldset">
              <label class="content__label">Conta</label>
              <v-text-field
                @keydown="preventSpecialCharacteres($event)"
                v-model="accountInfos.account"
                placeholder="Digite a conta"
                maxlength="10"
                :rules="[
                  (v) => v.length > 0 || 'Por favor, digite uma conta',
                  (v) => /.*-.+/.test(v) || 'Adicione o dígito da sua conta (ex: 12345-6)',
                  (v) => /^[a-zA-Z0-9-\-]+$/.test(v) || 'Digite apenas números, letras e o hífen',
                ]"
                outlined
                required
              />
            </div>
          </v-form>
        </div>

        <button
          id="por-que-precisamos-destes-dados"
          class="modal-button"
          type="button"
          @click="toggleModal"
        >
          <i class="modal-button__icon">i</i>
          <span class="modal-button__label">Por que precisamos destes dados?</span>
        </button>
      </section>
    </b-container>

    <FloatBar
      :mainPrice="maxOptionAllowedPriceDevice"
      :additionalPrice="maxOptionAllowedPriceBag"
    >
      <Button
        id="ir-para-pagamento"
        slot="firstButton"
        class="float-bar__button"
        theme="purple-filled"
        label="Ir para pagamento"
        :disabled="!goToPaymentButtonAvailable"
        @click="goToCheckout"
      />
    </FloatBar>

    <TheModal v-if="openModal" :open="openModal" :toggleModal="toggleModal">
      <ModalArticle
        generalTitle="Por que precisamos destes dados?"
      >
        <div>
          <p>Os dados da conta comprovam qual conta e cartão você escolheu para proteger. Caso você precise acionar, já sabemos pra qual conta sua cobertura está ativa :)</p>
          <div class="lgpd-text">
            <img class="lgpd-text__icon" src="../../assets/svg/lock-checkmark-square.svg" alt="Ícone cadeado" />
            <span>Seguimos a Lei Geral de Proteção de Dados - LGPD</span>
          </div>
        </div>
      </ModalArticle>
    </TheModal>
  </div>
</template>

<style lang="scss">
  #protected-bag-account {
    .v-input.v-text-field {

      .v-input__control .v-input__slot {
        background-color: white;
        border-color: #BDC2C6;
        color: #BDC2C6;
        border-radius: 8px;
        min-height: 48px;
        padding: 0 16px;

        fieldset {
          border-color: #BDC2C6;
        }
      }

      .v-text-field__details {
        padding: 0;
      }

      .v-messages.error--text {
        color: var(--danger);
      }

      .v-input__append-inner {
        margin-top: 12px;
      }

      .mdi-menu-down::before {
        content: "\F0140";
        color: #595959;
      }

      .v-menu__content {
        top: 100% !important;
        left: 0 !important;
      }
    }
  }
</style>

<style lang="scss" scoped>
  #protected-bag-account {
    min-height: 100vh;
    background: #F5F5F5;
    color: #3F4956;
    position: relative;

    @media (max-width: 767px) {
      padding-bottom: 158px;
    }
  }

  .content {
    margin: 40px 0;
    width: 100%;

    @media (max-width: 997px) {
      margin: 64px 0;
    }

    @media (max-width: 767px) {
      margin: 8px 0;
      grid-gap: 32px;
      gap: 32px;
    }

    &__title {
      font-size: 32px;
      font-weight: 700;
      line-height: 40px;
      max-width: 700px;
      margin-bottom: 40px;

      @media (max-width: 997px) {
        margin-bottom: 48px;
      }

      @media (max-width: 767px) {
        font-size: 24px;
        line-height: 28px;
        margin-bottom: 32px;
      }
    }

    &__mark {
      color: #6320E9;
      background-color: transparent;
      padding: 0;
    }

    &__form {
      width: 100%;
      margin-bottom: 10px;
    }

    &__fieldset {
      &:first-child {
        @media (min-width: 575px) and (max-width: 997px) {
          grid-column: span 2;
        }
      }
    }

    &__label {
      font-weight: bold;
    }
  }

  .v-form {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 16px;
    gap: 16px;

    @media (max-width: 997px) {
      grid-template-columns: repeat(2, 1fr);
      grid-gap: 0 16px;
      gap: 0 16px;
    }

    @media (max-width: 574px) {
      grid-template-columns: 1fr;
      grid-gap: 0;
      gap: 0;
    }
  }

  .modal-button {
    all: unset;
    cursor: pointer;
    margin-right: auto;
    font-family: 'Rubik', sans-serif;

    &__icon {
      display: inline-block;
      font-style: normal;
      width: 14px;
      height: 14px;
      font-size: 14px;
      font-weight: bold;
      line-height: 15px;
      text-align: center;
      color: #97979B;
      border: 2px solid #97979B;
      border-radius: 50%;
      margin-right: 8px;
      pointer-events: none;
    }

    &__label {
      line-height: 18px;
      pointer-events: none;
    }
  }

  .lgpd-text {
    color: #6320E9;
    display: inline-flex;

    &__icon {
      margin-right: 12px;
    }
  }
</style>
